import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { User } from '@auth0/auth0-react';

import { useAuthManagerContext } from '@private-crm-app/auth-manager-context';

import { getAuth0UserContact, getAuth0UserContactByPhone, getLoggedInUser, getAuth0UserByPhoneNumber, createContact, updateLoggedInUser } from '@private-crm-app/requests/user';

export interface UserContextValue {
  auth0User: User;
  user: IContactResponse;
  isLoading: boolean;
  updateUser: (contactId: number, contact: IContactRequest) => Promise<IContactResponse>;
  refreshUser: () => any;
  updateError: string;
}

export const defaultUserContextValue: UserContextValue = {
  auth0User: null,
  user: null,
  isLoading: false,
  updateUser: () => null,
  refreshUser: () => null,
  updateError: '',
};

type UserContextProviderProps = {
  children: React.ReactNode;
};

export const UserContext = createContext<UserContextValue>(defaultUserContextValue);

export function UserContextProvider({ children }: UserContextProviderProps): JSX.Element {
  const [user, setUser] = useState<IContactResponse>(null);

  const { auth0User, isLoading: isAuthLoading, refreshTrigger } = useAuthManagerContext();

  const [isLoading, setIsLoading] = useState(isAuthLoading);

  const [updateError, setUpdateError] = useState('');
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastProcessedTrigger, setLastProcessedTrigger] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!auth0User) {
      console.log('fetchUser called but auth0User is not available');
      setIsLoading(false);
      return;
    }

    // Prevent concurrent fetchUser executions
    if (isFetching) {
      console.log('fetchUser already in progress, skipping');
      return;
    }

    console.log('Fetching user data for:', auth0User.email || 'SMS user (no email yet)');
    setIsLoading(true);
    setIsFetching(true);

    try {
      if (auth0User.sub) {
        const isSMSUser = auth0User.sub.startsWith('sms|');
        const hasEmail = auth0User.email && auth0User.email !== 'undefined';

        if (isSMSUser && !hasEmail) {
          console.log('SMS user without email detected, attempting phone number lookup:', auth0User.sub);

          // Extract phone number from our application's JWT token (private_app_token)
          let phoneNumber = null;
          const jwtToken = localStorage.getItem('private_app_token');
          if (jwtToken) {
            try {
              // Decode JWT manually (it's a simple base64 decode for the payload)
              const payloadBase64 = jwtToken.split('.')[1];
              const payloadDecoded = atob(payloadBase64);
              const payload = JSON.parse(payloadDecoded);
              phoneNumber = payload?.phone as string;
              console.log(
                '[Phone Number Lookup] Phone from application JWT:',
                phoneNumber ? phoneNumber.substring(0, 6) + '...' : 'undefined'
              );
            } catch (error) {
              console.error('[Phone Number Lookup] Error decoding application JWT:', error);
            }
          } else {
            console.log('[Phone Number Lookup] No private_app_token found in localStorage');
          }

          // Try to get primary user by phone number using Auth0 Management API
          if (phoneNumber) {
            try {
              console.log(
                '[Phone Number Lookup] Attempting to find primary user by phone number via Auth0 Management API'
              );
              const phoneResponse = await getAuth0UserByPhoneNumber(phoneNumber, auth0User.sub);

              if (phoneResponse?.user?.email && phoneResponse.user.isPrimary) {
                console.log('[Phone Number Lookup] Found primary user with email:', phoneResponse.user.email);

                // Now use the primary user's email to get contact data
                const response = await getAuth0UserContact(phoneResponse.user.email, phoneResponse.user.user_id);
                if (response && response.individualContacts && response.individualContacts.length > 0) {
                  console.log(
                    '[Phone Number Lookup] Successfully retrieved primary user contact:',
                    response.individualContacts[0].id
                  );
                  setUser(response.individualContacts[0]);
                  return; // Exit early since we found the user
                }
              } else {
                console.log('[Phone Number Lookup] No primary user found or no email available');
              }
            } catch (phoneError) {
              console.error('[Phone Number Lookup] Error looking up user by phone:', phoneError);
            }
          } else {
            console.log('[Phone Number Lookup] No phone number found in Auth0 claims');
          }

          if (phoneNumber) {
            try {
              console.log('[SMS Contact] Searching for existing contact by phone and auth0UserId:', phoneNumber.substring(0, 6) + '...');
              const existingContactResponse = await getAuth0UserContactByPhone(phoneNumber, auth0User.sub);
              
              if (existingContactResponse?.individualContacts && existingContactResponse.individualContacts.length > 0) {
                const existingContact = existingContactResponse.individualContacts[0];
                console.log('[SMS Contact] Found existing contact with matching auth0UserId:', existingContact.id);
                setUser(existingContact);
                return;
              }

              console.log('[SMS Contact] No existing contact found with matching auth0UserId, creating new one');
              const contactPayload = {
                name: phoneNumber,
                contactType: 'INDIVIDUAL',
                isVendor: false,
                isGisPopulated: false,
                cellPhone: phoneNumber,
                notificationPreference: ['SMS'],
                isSourceAuth0: true,
                auth0UserId: auth0User.sub,
              };

              const newContact = await createContact(contactPayload, { skipCustomFields: true });
              if (newContact) {
                console.log('[SMS Contact] Successfully created new contact:', (newContact as any).id);
                setUser(newContact as IContactResponse);
                return;
              }
            } catch (contactError) {
              console.error('[SMS Contact] Error in contact creation/lookup:', contactError);
            }
          }

          // If all attempts failed, set user to null
          console.log('Unable to resolve SMS user via phone number lookup or contact creation, setting user to null');
          setUser(null);
        } else {
          console.log('Using auth0 user contact endpoint with sub:', auth0User.sub);
          const response = await getAuth0UserContact(auth0User.email, auth0User.sub);

          if (response && response.individualContacts && response.individualContacts.length > 0) {
            console.log('User contact found:', response.individualContacts[0].id);
            setUser(response.individualContacts[0]);
          } else {
            console.log('No user contact found for auth0 sub:', auth0User.sub);
            
            // Create new contact for email users
            try {
              console.log('[Email Contact] Creating new contact for email user');
              const nameToSend = auth0User.name && auth0User.name !== auth0User.email
                ? auth0User.name
                : auth0User.email.split('@')[0];

              const contactPayload = {
                name: nameToSend,
                contactType: 'INDIVIDUAL',
                isVendor: false,
                isGisPopulated: false,
                email: auth0User.email,
                notificationPreference: ['EMAIL'],
                isSourceAuth0: true,
                auth0UserId: auth0User.sub,
              };

              const newContact = await createContact(contactPayload, { skipCustomFields: true });
              if (newContact) {
                console.log('[Email Contact] Successfully created new contact:', (newContact as any).id);
                setUser(newContact as IContactResponse);
                return;
              }
            } catch (contactError) {
              console.error('[Email Contact] Error in contact creation:', contactError);
            }
            
            setUser(null);
          }
        }
      } else {
        console.log('Using legacy login user endpoint');
        const response = await getLoggedInUser(auth0User.email);

        if (response && response.individualContacts && response.individualContacts.length > 0) {
          setUser(response.individualContacts[0]);
        } else {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Error fetching user contact:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
      setFetchAttempted(true);
      setIsFetching(false);
    }
  }, [auth0User, isFetching]);

  const refreshUser = useCallback(async () => {
    if (isRefreshing) {
      console.log('User refresh already in progress, skipping');
      return;
    }

    setIsRefreshing(true);
    setFetchAttempted(false);
    try {
      await fetchUser();
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, fetchUser]);

  const updateUser = async (contactId: number, contact: IContactRequest) => {
    setUpdateError('');
    return updateLoggedInUser(contactId, contact)
      .then((response) => {
        setUser(response);
        return response;
      })
      .catch((error) => {
        console.error('error', error);
        const errorMessage = error.response?.data?.message || error.message;
        setUpdateError(errorMessage);
        throw error;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (auth0User && !fetchAttempted) {
      fetchUser();
    } else if (!auth0User) {
      setIsLoading(isAuthLoading);
    }
  }, [auth0User, isAuthLoading, fetchAttempted, fetchUser]);

  useEffect(() => {
    if (refreshTrigger > lastProcessedTrigger && refreshTrigger > 0 && auth0User && !isRefreshing) {
      console.log(`[SMS Account Linking] New refresh trigger detected (${refreshTrigger}), refreshing user data`);
      setLastProcessedTrigger(refreshTrigger);
      refreshUser();
    }
  }, [refreshTrigger, lastProcessedTrigger, auth0User, isRefreshing, refreshUser]);

  const value = useMemo<UserContextValue>(
    () => ({
      auth0User,
      user,
      isLoading: isLoading || isRefreshing,
      updateUser,
      refreshUser,
      updateError,
    }),
    [auth0User, user, isLoading, isRefreshing, updateError, refreshUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw Error('Component is not inside UserContext Provider');
  }

  return context;
}
